import type { MemberType, Role, DuesStatus, PaymentMethod } from '@gwc/shared';
import { api } from '../lib/api';
import type { MemberRow, MemberStats, DueRow, DuesSummary } from './models';

export interface InviteInput {
  email: string;
  firstName: string;
  lastName: string;
  memberType?: MemberType | null;
  roles?: Role[];
}

export interface MemberPatch {
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  memberType?: MemberType | null;
  memberSince?: string | null;
  accountStatus?: string | null;
  roles?: Role[];
  notes?: string | null;
}

export interface ChargeDuesInput {
  label: string;
  amount: number;
  periodYear?: number | null;
  dueDate?: string | null;
  status?: DuesStatus;
  notes?: string | null;
}

export interface DuesPatch {
  label?: string;
  amount?: number;
  status?: DuesStatus;
  dueDate?: string | null;
  paidAt?: string | null;
  paymentMethod?: PaymentMethod | null;
  notes?: string | null;
}

export interface PersonLookupRow {
  id: string;
  firstName: string;
  lastName: string;
  isMember: boolean;
  memberType: string | null;
  /** Admin-only fields. */
  accountStatus?: string | null;
  email?: string | null;
}

export interface InviteExistingInput {
  email?: string;
  memberType?: MemberType;
  roles?: Role[];
}

export const membersApi = {
  list: () => api.get<{ members: MemberRow[] }>('/members').then((r) => r.members),
  /** Every person in the records — members and imported non-members alike. */
  lookup: (q: string, limit = 20) =>
    api
      .get<{ people: PersonLookupRow[] }>(`/members/lookup?q=${encodeURIComponent(q)}&limit=${limit}`)
      .then((r) => r.people),
  /** Invite a person who is already in the records but has no login (a past member). */
  inviteExisting: (id: string, body: InviteExistingInput) =>
    api.post<{ member: MemberRow; inviteToken: string; acceptUrl: string; emailSent: boolean }>(
      `/members/${id}/invite`,
      body,
    ),
  get: (id: string) => api.get<{ member: MemberRow; stats: MemberStats }>(`/members/${id}`),
  invite: (body: InviteInput) =>
    api.post<{ member: MemberRow; inviteToken: string; acceptUrl: string; emailSent: boolean }>(
      '/members/invite',
      body,
    ),
  resendInvite: (id: string) =>
    api.post<{ acceptUrl: string; emailSent: boolean }>(`/members/${id}/resend-invite`),
  /** Email an active member a password reset link (the link is never shown to the admin). */
  sendPasswordReset: (id: string) =>
    api.post<{ emailSent: boolean; email: string }>(`/members/${id}/send-password-reset`),
  update: (id: string, patch: MemberPatch) =>
    api.patch<{ member: MemberRow }>(`/members/${id}`, patch),

  dues: (id: string) =>
    api.get<{ dues: DueRow[]; summary: DuesSummary }>(`/members/${id}/dues`),
  charge: (id: string, body: ChargeDuesInput) =>
    api.post<{ due: DueRow }>(`/members/${id}/dues`, body),
  updateDue: (id: string, dueId: string, patch: DuesPatch) =>
    api.patch<{ due: DueRow }>(`/members/${id}/dues/${dueId}`, patch),
  deleteDue: (id: string, dueId: string) => api.del<{ ok: true }>(`/members/${id}/dues/${dueId}`),
};
